import { Box, CircularProgress, List, Modal, Typography, useMediaQuery, useTheme } from '@mui/material';
import SidebarItem from 'components/Layouts/components/SidebarItem';
import { behavior, configure, home } from 'routes/config';
import * as urls from 'routes/config';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import IdentityIcon from 'assets/icons/IdentityIcon';
import KnowledgeIcon from 'assets/icons/KnowledgeIcon';
import DeployWebIcon from 'assets/icons/DeployWebIcon';
import PersonalizedLinkIcon from 'assets/icons/PersonalizedLinkIcon';
import { useEffect, useState } from 'react';
import SupportAgentIcon from '@/assets/icons/SupportAgentIcon';
import BehaviorIcon from '@/assets/icons/BehaviorIcon';
import TalkButton from '@/components/Common/TalkButton';
import { RxCross1 } from 'react-icons/rx';

export default function Configure() {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { locationId, agentId } = useParams();
  const promptTreePath = `${urls.agentBase(locationId, agentId)}/${home.configure}/${configure.behavior}/${behavior.instructions}/prompt-tree`;
  const isKnowledgeConfigure = pathname.includes(`/configure/${configure.knowledge}`);
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Gathering Information');
  const [isModalOpen, setIsModalOpen] = useState(localStorage.getItem('configuringAgent') === 'true');
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [isModalHide2, setIsModalHide2] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('configuringAgent') === 'true') {
      const labels = ['Gathering Information', 'Analyzing Content', 'Optimizing Insights'];
      const duration = 10000;
      const interval = duration / 100;

      const timer = setInterval(() => {
        setProgress((prevProgress) => {
          const nextProgress = prevProgress + 1;
          if (nextProgress <= 33) setLabel(labels[0]);
          else if (nextProgress <= 66) setLabel(labels[1]);
          else setLabel(labels[2]);

          return nextProgress >= 100 ? 100 : nextProgress;
        });
      }, interval);

      if (progress === 100 && localStorage.getItem('configuringAgent') === 'true') {
        localStorage.removeItem('configuringAgent');
        setIsModalOpen(false);
        setIsModalOpen2(true);
        clearInterval(timer);
      }

      return () => clearInterval(timer);
    } else {
      if (localStorage.getItem('configuringAgent') === 'withoutWebUrl') {
        localStorage.removeItem('configuringAgent');
        setIsModalOpen2(true);
      }
    }
  }, [progress]);

  return (
    <>
      <Box display='flex' gap={4} sx={{ width: '100%' }} minHeight='calc(100vh - 64px)'>
        {!isMediumScreen ? (
          <List
            sx={{
              minWidth: 262,
              maxWidth: 262,
              height: 'fit-content',
              background: theme.palette.background.paper,
              borderRadius: 2,
              position: 'sticky',
              top: '120px',
              alignSelf: 'flex-start',
              flexShrink: 0
            }}
          >
            <SidebarItem to={configure.identity} icon={<BehaviorIcon />} label='Identity' />

            <SidebarItem to={configure.knowledge} icon={<KnowledgeIcon />} label='Knowledge' disabled={!agentId} />
            <SidebarItem to={configure.deployWeb} icon={<DeployWebIcon />} label='Connect Web' disabled={!agentId} />
            <SidebarItem to={configure.personalizeLink} icon={<PersonalizedLinkIcon />} label='Personalized Link' />
          </List>
        ) : (
          <List
            className='mobile-sidebar'
            sx={{
              display: 'flex',
              height: 'fit-content',
              background: theme.palette.background.paper,
              position: 'fixed',
              bottom: '0px',
              overflow: 'auto',
              zIndex: '1000',
              backgroundColor: theme.palette.background.default,
              maxHeight: '5rem',
              width: '-webkit-fill-available',
              marginRight: '24px'
            }}
          >
            <SidebarItem type='mobile' to={configure.identity} icon={<IdentityIcon />} label='Identity' />

            <SidebarItem
              type='mobile'
              to={configure.knowledge}
              icon={<KnowledgeIcon />}
              label='Knowledge'
              disabled={!agentId}
            />
            <SidebarItem
              type='mobile'
              to={promptTreePath}
              icon={<BehaviorIcon />}
              label='Behavior'
              disabled={!agentId}
            />
            <SidebarItem
              type='mobile'
              to={configure.deployWeb}
              icon={<DeployWebIcon />}
              label='Connect Web'
              disabled={!agentId}
            />
            <SidebarItem
              type='mobile'
              to={configure.personalizeLink}
              icon={<PersonalizedLinkIcon />}
              label='Personalized Link'
              disabled={!agentId}
            />
          </List>
        )}
        <Box
          sx={{
            width: '100%',
            ...(isKnowledgeConfigure ? { minWidth: 0, flex: 1 } : {})
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <Modal open={isModalOpen} aria-labelledby='modal-modal-title' aria-describedby='modal-modal-description'>
        <Box sx={style}>
          <CircularProgressWithLabel value={progress} label={label} />
          <Typography fontSize={14} fontWeight={500} textAlign='center' marginTop={2}>
            We’re setting up your agent with the selected configuration and preferences.
          </Typography>
        </Box>
      </Modal>
      <Modal
        open={isModalOpen2}
        sx={{ display: isModalHide2 ? 'none' : '' }}
        onClose={() => setIsModalOpen2(false)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={{ ...style, position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              cursor: 'pointer'
            }}
            onClick={() => setIsModalOpen2(false)}
          >
            <RxCross1 size={18} />
          </Box>
          <SupportAgentIcon height={44} width={44} color={theme.palette.primary.main} />
          <Typography variant='h6' fontWeight={500} textAlign='center' marginTop={1}>
            Congratulation, Your Agent Is Ready to Go!
          </Typography>
          <Typography color='secondary' fontSize={14}>
            AI agent ready! Explore and adjust features as needed.
          </Typography>
          <Box display='flex' justifyContent='center' gap={2} mt={3}>
            <TalkButton label='Test Agent' sx={{ width: '150px' }} onClick={() => setIsModalHide2(true)} />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 3,
  borderRadius: '8px',
  textAlign: 'center',
  width: {
    xs: '90%',
    sm: 468
  }
};

function CircularProgressWithLabel(props: { value: number; label: string }) {
  return (
    <Box position='relative' display='inline-flex'>
      <CircularProgress variant='determinate' value={props.value} size={160} thickness={4} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          fontSize={14}
          color='secondary'
          textAlign='center'
          maxWidth='70%'
          sx={{
            wordBreak: 'break-word'
          }}
        >
          {props.label}
        </Typography>
      </Box>
    </Box>
  );
}
